/**
 * Pure utility functions for viewport pin logic.
 * No store dependencies — safe to import from components and lib alike.
 */

import type { SimTime, ViewportPin } from '../types';
import { simTimeToSeconds } from '../types';

/** Returns true if pin is the default start pin (time === "default-start") */
export function isDefaultStartPin(pin: ViewportPin): boolean {
  return pin.time === 'default-start';
}

/**
 * Get the effective SimTime for a viewport pin.
 * Resolves "default-start" to the scenario's startTime.
 */
export function getEffectiveTime(pin: ViewportPin, startTime: SimTime): SimTime {
  return pin.time === 'default-start' ? startTime : pin.time;
}

/**
 * Get the active viewport pin for a given simulation time.
 * Returns the most recent pin at or before the given time.
 * Pins must be pre-sorted (default-start first, then ascending by time).
 *
 * @param pins - The viewport pins array (e.g. scenario.viewportPins)
 * @param time - Current simulation time
 * @param startTime - Scenario start time (used to resolve "default-start")
 */
export function getActiveViewportPin(
  pins: ViewportPin[],
  time: SimTime,
  startTime: SimTime,
): ViewportPin | null {
  if (pins.length === 0) return null;

  const currentSeconds = simTimeToSeconds(time);
  let activePin: ViewportPin | null = null;

  for (const pin of pins) {
    const effectiveTime = getEffectiveTime(pin, startTime);
    const pinSeconds = simTimeToSeconds(effectiveTime);
    if (pinSeconds <= currentSeconds) {
      activePin = pin;
    } else {
      break; // pins are sorted, stop early
    }
  }

  return activePin;
}
